/**
 * Relay, Switch, Roller, Emeter, Thermostat, Lock, Breaker action preparations.
 * Duplicated from Scenes for Flow independence.
 */

import { convertUiPropertyToSceneCommand } from '../utilities';
import type {
	DeviceActionContext,
	PreparedDeviceAction,
	RelayActionHandler,
	RelayHandlerResult,
} from './types';
import type { DeviceAction } from 'src/types/DeviceActionUI';

const resolveRelayChannel = (context: DeviceActionContext, action: DeviceAction): number => {
	let channel = context.channel;
	if (typeof action?.addOnInd === 'number') {
		channel = action.addOnInd;
	}
	if (context.mode === 'emeter' && action.name === 'turn') {
		channel = 0;
	}
	return channel;
};

const createRelayHandlerResult = (
	context: DeviceActionContext,
	action: DeviceAction,
	channelOverride?: number,
): RelayHandlerResult => {
	const channel = channelOverride ?? resolveRelayChannel(context, action);
	return {
		command: convertUiPropertyToSceneCommand(action.name, channel),
		value: action.value,
	};
};

const defaultRelayActionHandler: RelayActionHandler = (context, action) =>
	createRelayHandlerResult(context, action);

const handleRelayTurnAction: RelayActionHandler = (context, action) => {
	const channelOverride = context.mode === 'emeter' ? 0 : undefined;
	return createRelayHandlerResult(context, action, channelOverride);
};

const RELAY_ACTION_HANDLERS: Partial<Record<string, RelayActionHandler>> = {
	turn: handleRelayTurnAction,
	state: handleRelayTurnAction,
};

export const prepareRelayAction = (context: DeviceActionContext): PreparedDeviceAction => {
	const { hexToDecimal, actions } = context;
	const primaryAction = actions[0];

	if (!primaryAction) {
		return { _gui_purpose: 'action' };
	}

	const handler = RELAY_ACTION_HANDLERS[primaryAction.name] ?? defaultRelayActionHandler;
	const { command, value } = handler(context, primaryAction);

	return {
		_gui_purpose: 'action',
		r: 'dev:' + hexToDecimal + ':' + command,
		set: value,
	};
};

export const isRelayLikeMode = (mode: string): boolean =>
	[
		'relay',
		'output_reader',
		'roller',
		'emeter',
		'thermostat',
		'monophase',
		'triphase',
		'lock',
		'breaker',
	].includes(mode);
